import { useState, useEffect } from 'react'
import { Loader2, Sparkles } from 'lucide-react'
import Modal from '@/components/common/Modal'

interface Props {
  open: boolean
  boundObject: string
  triggers: string[]
  onClose: () => void
  onGenerate: (description: string) => Promise<void> | void
  loading: boolean
}

/**
 * "Generate with AI" dialog for the graphical Workflow Designer: the user
 * describes the approval flow in natural language and the backend returns
 * XPDL that the page loads onto the canvas. The bound transaction object and
 * its triggers (read-only here) come from the workflow's approval binding —
 * the generation is always scoped to that object.
 */
export default function GenerateWorkflowModal({
  open, boundObject, triggers, onClose, onGenerate, loading,
}: Props) {
  const [description, setDescription] = useState('')

  // Reset the draft each time the dialog opens.
  useEffect(() => {
    if (open) setDescription('')
  }, [open])

  const canGenerate = description.trim().length > 0 && !loading

  const handleGenerate = () => {
    if (!canGenerate) return
    void onGenerate(description.trim())
  }

  return (
    <Modal open={open} onClose={onClose} title="Generate workflow with AI" size="wide">
      <div style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)', marginBottom: 12 }}>
        Describe the approval flow in plain language and we'll draft the workflow
        XPDL for you to review and edit on the canvas. The generated workflow
        replaces the current one — Save when you're happy with it.
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Approves transaction</label>
        <div style={boundChipStyle}>
          <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>{boundObject}</span>
          {triggers.length > 0 && (
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {' · '}{triggers.join(' / ')}
            </span>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Description</label>
        <textarea
          autoFocus
          value={description}
          onChange={e => setDescription(e.target.value)}
          disabled={loading}
          rows={6}
          placeholder="e.g. Orders over ₹100,000 need manager then finance approval; email the submitter when it's approved."
          style={textareaStyle}
        />
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', opacity: 0.7, marginTop: 4, display: 'block' }}>
          Mention who approves, in what order, any thresholds or conditions, and
          what should happen on approval or rejection.
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button onClick={onClose} className="btn btn-ghost btn-sm" disabled={loading}>Cancel</button>
        <button onClick={handleGenerate} className="btn btn-primary btn-sm" disabled={!canGenerate} style={{ gap: 6 }}>
          {loading
            ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
            : <Sparkles size={14} />}
          {loading ? 'Generating…' : 'Generate'}
        </button>
      </div>
    </Modal>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  marginBottom: 2,
  fontWeight: 500,
}

const boundChipStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: '0.8825rem',
  background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 4,
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px',
  fontSize: '0.8825rem',
  background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  color: 'var(--color-text)',
  outline: 'none',
  boxSizing: 'border-box',
  resize: 'vertical',
  fontFamily: 'inherit',
  lineHeight: 1.5,
}
